<template>
  <div class="forget-password-container">
    <div class="container-title">{{ $t('login.common.title.forgetPassword' ) }}</div>
    <div class="content-form forget-password-form">
      <div v-if="resetBy === RESET_BY.EMAIL" class="form-line" :class="getErrorClass(message.email)">
        <el-input
          v-model="email"
          class="line-input"
          :placeholder="$t('login.common.label.email')"
          @focus="clearErrorMessage(message.email)"
          @blur="validate(message.email)"
          @change="validate(message.email)"
        ></el-input>
        <div v-show="message.email.show" class="line-tip">{{ selectLabelI18n(message.email, 'text') }}</div>
      </div>
      <div v-if="resetBy === RESET_BY.PHONE" class="form-line" :class="getErrorClass(message.phone)">
        <el-input
          v-model="phone"
          class="line-input"
          :placeholder="$t('login.common.label.chinesePhone')"
          @focus="clearErrorMessage(message.phone)"
          @blur="validate(message.phone)"
          @change="validate(message.phone)"
        ></el-input>
        <div v-show="message.phone.show" class="line-tip">{{ selectLabelI18n(message.phone, 'text') }}</div>
      </div>
      <div v-if="resetBy === RESET_BY.EMAIL" class="form-line" :class="getErrorClass(message.captcha)">
        <div class="line-captcha-input">
          <el-input
            v-model="captcha"
            class="line-input captcha-input"
            :placeholder="$t('login.common.label.captcha')"
            @focus="clearErrorMessage(message.captcha)"
            @blur="validate(message.captcha)"
            @change="validate(message.captcha)"
          ></el-input>
          <img class="captcha-img" :src="captchaSrc"/>
          <i class="refresh-icon iconfont iconrefresh" @click="onRefreshClick"></i>
        </div>
        <div v-show="message.captcha.show" class="line-tip">{{ selectLabelI18n(message.captcha, 'text') }}</div>
      </div>
      <div v-if="resetBy === RESET_BY.PHONE" class="form-line" :class="getErrorClass(message.verifyCode)">
        <PhoneVerifyCodeInput
          v-model="verifyCode"
          class="line-input"
          :phone="phone"
          :captchaIndex="1"
          :placeholder="$t('login.common.label.verifyCode')"
          @focus="clearErrorMessage(message.verifyCode)"
          @blur="validate(message.verifyCode)"
          @change="validate(message.verifyCode)"
          @sendVerifySuccess="handelSendVerifySuccess"
        />
        <div v-show="message.verifyCode.show" class="line-tip">{{ selectLabelI18n(message.verifyCode, 'text') }}</div>
      </div>
    </div>
    <div class="button-container">
      <el-button class="submit-btn" type="primary" @click="onSubmitBtnClick">{{ $t('common.btn.submit') }}</el-button>
    </div>
    <div v-if="accountType !== ACCOUNT_TYPE.HIRING_MANAGER" class="form-bottom">
      <div class="bottom-line">
        <div class="change-reset-way">
          <template v-for="(label, index) in $t('login.common.message.resetTip.label')">
            <router-link
              v-if="label === 'type_link_html'"
              class="change-link link"
              :key="index"
              :to="{ query: { ...$route.query, reset_by: anotherResetBy }, hash: $route.hash }"
            >{{ $t(`login.common.message.resetTip.type.${anotherResetBy}`) }}</router-link>
            <span v-else :key="index">{{ label }}</span>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import _ from 'lodash'
import { mapActions } from 'vuex'
import { ACCOUNT_TYPE } from '@/constant/common'
import { validateEmail, validatePhone } from '@/utils/validator'
import PhoneVerifyCodeInput from '@/components/formEditor/phoneVerifyCodeInput.vue'
import { selectLabelI18n } from '@/i18n'

const RESET_BY = {
  PHONE: 'phone',
  EMAIL: 'email'
}

export default {
  components: {
    PhoneVerifyCodeInput
  },
  props: {
    company: {
      type: Object,
      default: null
    }
  },
  data () {
    return {
      selectLabelI18n,
      ACCOUNT_TYPE,
      RESET_BY,
      email: null,
      phone: null,
      verifyCode: null,
      captcha: null,
      verifyCodeCountdown: -1,
      captchaSrc: null,
      hasSentVerifyCode: false,
      message: {
        email: {
          show: false,
          isError: false,
          text: null,
          textI18nKey: null
        },
        phone: {
          show: false,
          isError: false,
          text: null,
          textI18nKey: null
        },
        verifyCode: {
          show: false,
          isError: false,
          text: null,
          textI18nKey: null
        },
        captcha: {
          show: false,
          isError: false,
          text: null,
          textI18nKey: null
        }
      }
    }
  },
  computed: {
    accountType () {
      return this.$route.params.accountType
    },
    resetBy () {
      return this.$route.query.reset_by
    },
    anotherResetBy () {
      return this.resetBy === RESET_BY.EMAIL ? RESET_BY.PHONE : RESET_BY.EMAIL
    }
  },
  created () {
    this.$eventBus.$emit('showLoading', true)
    if (!this.resetBy) {
      this.$router.replace({ query: { ...this.$route.query, reset_by: RESET_BY.EMAIL }, hash: this.$route.hash })
    } else if (this.accountType === ACCOUNT_TYPE.HIRING_MANAGER && this.resetBy !== RESET_BY.EMAIL) {
      this.$router.replace({ query: { ...this.$route.query, reset_by: RESET_BY.EMAIL }, hash: this.$route.hash })
    } else if (this.resetBy === RESET_BY.EMAIL) {
      this.onRefreshClick()
    }
    this.$eventBus.$emit('showLoading', false)
  },
  methods: {
    ...mapActions('account', ['forgetPassword']),
    ...mapActions('hm', ['forgetHmPassword']),
    getErrorClass (message) {
      return message && message.show && message.isError && 'has-error'
    },
    setErrorMessage (message, textI18nKey, getter) {
      if (getter) {
        Object.defineProperty(message, 'text', {
          configurable: true,
          enumerable: true,
          get: getter
        })
      } else {
        message.text = null
      }
      message.isError = true
      message.textI18nKey = textI18nKey
      message.show = true
    },
    clearErrorMessage (message) {
      message.show = false
      message.isError = false
      message.text = null
      message.textI18nKey = null
    },
    validate (message) {
      if (!message) {
        return _.chain(this.message).map(this.validate).includes(false).value()
      }
      let hasError = false
      let textI18nKey = null
      const getter = null
      if (message === this.message.email && this.resetBy === RESET_BY.EMAIL) {
        if (this.email) {
          if (validateEmail(null, this.email)) {
            hasError = true
            textI18nKey = 'common.message.invalidEmail'
          }
        } else {
          hasError = true
          textI18nKey = 'login.common.placeholder.email'
        }
      } else if (message === this.message.phone && this.resetBy === RESET_BY.PHONE) {
        if (this.phone) {
          if (validatePhone(null, this.phone)) {
            hasError = true
            textI18nKey = 'common.message.invalidMobile'
          }
        } else {
          hasError = true
          textI18nKey = 'login.common.placeholder.phone'
        }
      } else if (message === this.message.verifyCode && this.resetBy === RESET_BY.PHONE) {
        if (this.hasSentVerifyCode) {
          if (!this.verifyCode) {
            hasError = true
            textI18nKey = 'login.common.placeholder.smsCode'
          }
        } else {
          hasError = true
          textI18nKey = 'login.common.message.getVerifyCodeFirst'
        }
      } else if (message === this.message.captcha && this.resetBy === RESET_BY.EMAIL) {
        if (!this.captcha) {
          hasError = true
          textI18nKey = 'login.common.placeholder.captcha'
        }
      }
      if (hasError) {
        this.setErrorMessage(message, textI18nKey, getter)
        return false
      } else {
        this.clearErrorMessage(message)
        return true
      }
    },
    onRefreshClick () {
      this.captchaSrc = this.$DjangoBaseUrl('/generate-captcha/1/?ts=' + new Date().valueOf())
    },
    handelSendVerifySuccess () {
      this.hasSentVerifyCode = true
    },
    async onSubmitBtnClick () {
      if (this.accountType === ACCOUNT_TYPE.HIRING_MANAGER) {
        this.onHMForgetPassword()
        return
      }
      this.$eventBus.$emit('showLoading', true)
      try {
        const hasError = this.validate()
        if (hasError) {
          return
        }
        const resp = await this.forgetPassword({
          email_address: this.email,
          phone: this.phone,
          sms_validation_code: this.verifyCode,
          captcha: this.captcha,
          captcha_index: 1,
          find_type: this.resetBy
        })
        if (resp) {
          if (this.resetBy === RESET_BY.PHONE) {
            this.$router.push({
              path: `/passport/${this.accountType}/reset-password/${resp}`,
              query: this.$route.query
            })
          } else {
            this.$message.success({ message: this.$t('login.common.message.sentResetPasswordEmail'), duration: 5000, showClose: true })
            this.$router.push({
              path: `/passport/${this.accountType}/login`,
              query: {
                ...this.$route.query,
                reset_by: undefined
              }
            })
          }
        }
      } catch (err) {
        if (err.code === 20006002) {
          // this.setErrorMessage(this.resetBy === RESET_BY.EMAIL ? this.message.email : this.message.phone, 'login.common.message.userIsNotExists')
          if (this.accountType === ACCOUNT_TYPE.CANDIDATE) {
            this.$message.error(this.$t('login.common.message.candidateIsNotExists'))
          } else {
            this.$message.error(this.$t('login.common.message.userIsNotExists'))
          }
        }
        throw err
      } finally {
        this.$eventBus.$emit('showLoading', false)
      }
    },
    async onHMForgetPassword () {
      this.$eventBus.$emit('showLoading', true)
      try {
        const hasError = this.validate()
        if (hasError) {
          return
        }
        const resp = await this.forgetHmPassword({
          email_address: this.email,
          captcha: this.captcha,
          captcha_index: 1
        })
        if (resp) {
          this.$message.success({ message: this.$t('login.common.message.sentResetPasswordEmail'), duration: 5000, showClose: true })
          this.$router.push({
            path: `/passport/${this.accountType}/login`,
            query: {
              ...this.$route.query,
              reset_by: undefined
            }
          })
        }
      } finally {
        this.$eventBus.$emit('showLoading', false)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.forget-password-container {
  .container-title {
    padding-top: 38px;
  }

  .forget-password-form {
    margin-top: 35px;

    .form-line {
      min-height: 40px;
      margin-bottom: 24px;

      &.has-error {
        margin-bottom: 6px;

        .line-tip {
          padding-left: 0;
          color: #f56c6c;
        }

        .line-input {
          ::v-deep {
            > .el-input__inner {
              border-color: #f56c6c;
            }

            .verify-code {
              .el-input__inner {
                border-color: #f56c6c;
              }
            }

            .el-input__inner:hover + .el-input-group__prepend,
            .el-input__inner:hover + .el-input-group__append,
            .el-input-group__prepend,
            .el-input-group__append {
              border-color: #f56c6c;
            }
          }
        }
      }

      .sub-form-line {
        display: inline-block;
        width: 200px;
        height: 40px;
        vertical-align: middle;

        &.has-error {
          .line-tip {
            padding-left: 0;
            color: #f56c6c;
          }

          .line-input {
            ::v-deep {
              .el-input__inner {
                border-color: #f56c6c;
              }
            }
          }
        }

        + .sub-form-line {
          margin-left: 20px;
        }
      }

      .line-input {
        ::v-deep {
          .el-input__inner {
            border-radius: 2px;
            padding-left: 14px;
            padding-right: 35px;

            &:hover + .el-input-group__append {
              border-color: #c0c4cc;
            }

            &:focus + .el-input-group__append {
              border-color:  var(--custom-color, #0096cc);
            }

            &:focus {
              border-color:  var(--custom-color, #0096cc);
            }
          }

          .el-input-group__append {
            padding-left: 0;
            padding-right: 14px;
          }

          .send-verify-code {
            color: var(--custom-color, #0096cc);
            cursor: pointer;

            &.has-sent {
              cursor: default;
            }
          }

          //

          .el-input__suffix {
            right: 14px;
          }

          .el-input-group__prepend {
            border-top-left-radius: 2px;
            border-bottom-left-radius: 2px;
            background: initial;
            transition: border-color 0.2s cubic-bezier(0.645, 0.045, 0.355, 1)
          }

          .el-input-group__append {
            border-top-right-radius: 2px;
            border-bottom-right-radius: 2px;
            background: initial;
            transition: border-color 0.2s cubic-bezier(0.645, 0.045, 0.355, 1)
          }
        }
      }

      .line-captcha-input {
        .captcha-input {
          width: 200px;
          vertical-align: middle;
        }

        .captcha-img {
          display: inline-block;
          width: 135px;
          height: 40px;
          line-height: 40px;
          margin-left: 25px;
          vertical-align: middle;
        }

        .refresh-icon {
          margin-left: 10px;
          font-size: 30px;
          vertical-align: middle;
          cursor: pointer;
        }
      }

      .line-tip {
        padding-left: 15px;
        font-size: 12px;
        line-height: 18px;
      }
    }
  }

  .button-container {

    .el-button {
      width: 100%;
      padding: 9px 0;
      border-radius: 2px;
      line-height: 22px;
      font-size: 16px;
      text-align: center;
      border: 0;
      background-color: var(--custom-color, #0096cc);
    }
  }

  .form-bottom {
    margin-top: 20px;

    .bottom-line {
      margin-bottom: 10px;

      .change-reset-way {
        .link {
          color: var(--custom-color, #0096cc)
        }
      }
    }
  }
}
.is-mobile {
  .forget-password-container {
    .container-title {
      font-size: 30px;
      line-height: 44px;
    }

    .forget-password-form {
      .form-line {
        .line-captcha-input {
          display: flex;
          align-items: center;

          .captcha-input {
            flex: 1;
          }

          .captcha-img {
            width: 100px;
          }
        }
      }
    }
  }
}

.has-company-info {
  .forget-password-container {
    .container-title {
      padding-top: 0;
    }
  }
}
</style>
